import {FC, Fragment, useState} from 'react'
import {numberWithCommas} from '../../../../../../../_metronic/helpers'
import {InvoicePaymentForm, InvoicePaymentTypeItems} from '../../core/_models'
import {
  getPaymentRegisteredAmount,
  getPaymentTaxRegistrationBadge,
} from '../../core/paymentTaxRegistration'
import {CopyableInvoiceValue, InvoiceDetailsGrid} from '../components/InvoicePresentation'

type Props = {
  payment: InvoicePaymentForm
  index: number
  columnCount: number
  showReference: boolean
  taxValidationError?: string
  isEditing?: boolean
  onEdit: () => void
  onDelete: () => void
  // ردیف ثبت‌شده در سامانه مؤدیان تلاش مجدد می‌گیرد (فقط برای ردیف‌های ردشده/نامشخص/ناموفق)
  onRetryRegistration?: (registrationId: number) => void
  retryingRegistrationId?: number
  // صورتحساب ارسال‌شده: مبلغی که به کارپوشه می‌رود (بدون مالیات) در جزئیات نشان داده می‌شود
  isSentInvoice?: boolean
}

const InvoicePaymentRow: FC<Props> = ({
  payment,
  index,
  columnCount,
  showReference,
  taxValidationError,
  isEditing = false,
  onEdit,
  onDelete,
  onRetryRegistration,
  retryingRegistrationId,
  isSentInvoice = false,
}) => {
  const [expanded, setExpanded] = useState(false)
  const referenceNumber = payment.traceNo || payment.refereceNo || payment.trackingNumber || ''
  const paymentMethod =
    InvoicePaymentTypeItems.find((item) => item.value === payment.type)?.label ||
    payment.paymentMethod ||
    'روش نامشخص'
  const locked = payment.locked === true
  const registrationBadge = getPaymentTaxRegistrationBadge(payment.taxRegistration, locked)
  const registrationId = payment.taxRegistration?.registrationId
  const canRetry =
    !!registrationBadge?.canRetry && !!registrationId && typeof onRetryRegistration === 'function'
  const isRetrying = !!registrationId && retryingRegistrationId === registrationId
  const rowMessage = registrationBadge?.message
  const rowMessageTone =
    payment.taxRegistration?.status === 3
      ? 'has-error'
      : payment.taxRegistration?.status === 4 || payment.taxRegistration?.status === 6
      ? 'has-warning'
      : ''

  return (
    <Fragment>
      <tr
        className={`mp-invoice-data-row${expanded ? ' is-expanded' : ''}${
          taxValidationError ? ' has-error' : ''
        }${isEditing ? ' is-editing' : ''}${locked ? ' is-locked' : ''}`}
      >
        <td className='mp-invoice-cell--index text-center' data-label='ردیف'>
          <span className='mp-invoice-row-number'>{index + 1}</span>
        </td>
        <td className='mp-invoice-cell--item text-start' data-label='روش پرداخت'>
          <strong className='mp-invoice-item-title'>
            {locked && (
              <i
                className='las la-lock text-gray-500 me-1'
                title='این ردیف در سامانه مؤدیان ثبت شده و قابل تغییر نیست'
                aria-hidden='true'
              ></i>
            )}
            {paymentMethod}
          </strong>
          {payment.description && <small title={payment.description}>{payment.description}</small>}
        </td>
        <td className='mp-invoice-cell--money text-center' data-label='مبلغ پرداخت'>
          <b>{numberWithCommas(payment.price)}</b>
          <small>ریال</small>
        </td>
        <td className='mp-invoice-cell--date text-center' data-label='تاریخ پرداخت'>
          <span className='englishText' dir='ltr'>
            {payment.date || '—'}
          </span>
        </td>
        {showReference && (
          <td className='mp-invoice-cell--reference text-center' data-label='شماره پیگیری یا مرجع'>
            {referenceNumber ? (
              <CopyableInvoiceValue value={referenceNumber} label='شماره پیگیری یا مرجع' />
            ) : (
              '—'
            )}
          </td>
        )}
        <td className='mp-invoice-cell--status text-center' data-label='وضعیت'>
          {registrationBadge ? (
            <span
              className={`badge ${registrationBadge.className}`}
              title={
                payment.taxRegistration?.date
                  ? `آخرین اقدام: ${payment.taxRegistration.date}`
                  : undefined
              }
            >
              <i className={`las ${registrationBadge.icon}`} aria-hidden='true'></i>
              {registrationBadge.label}
            </span>
          ) : (
            <span
              className={`badge ${
                taxValidationError ? 'badge-light-warning' : 'badge-light-success'
              }`}
            >
              <i
                className={`las ${
                  taxValidationError ? 'la-exclamation-triangle' : 'la-check-circle'
                }`}
                aria-hidden='true'
              ></i>
              {taxValidationError ? 'نیازمند اصلاح' : 'آماده ثبت'}
            </span>
          )}
        </td>
        <td className='mp-invoice-cell--actions text-center' data-label='عملیات'>
          <div className='mp-invoice-row-actions'>
            <button
              type='button'
              className='btn btn-icon btn-sm btn-light-primary'
              title={expanded ? 'بستن جزئیات' : 'مشاهده جزئیات'}
              aria-label={expanded ? 'بستن جزئیات پرداخت' : 'مشاهده جزئیات پرداخت'}
              aria-expanded={expanded}
              onClick={() => setExpanded((current) => !current)}
            >
              <i
                className={`las ${expanded ? 'la-angle-up' : 'la-angle-down'}`}
                aria-hidden='true'
              ></i>
            </button>
            {canRetry && (
              <button
                type='button'
                className='btn btn-icon btn-sm btn-light-warning'
                title={
                  registrationBadge?.retryNeedsCheck
                    ? 'تلاش مجدد ثبت در سامانه (پیش از آن، کارپوشه را بررسی کنید)'
                    : 'تلاش مجدد ثبت در سامانه مؤدیان'
                }
                aria-label='تلاش مجدد ثبت پرداخت در سامانه مؤدیان'
                disabled={isRetrying}
                onClick={() => registrationId && onRetryRegistration?.(registrationId)}
              >
                <i
                  className={`las ${isRetrying ? 'la-spinner la-spin' : 'la-redo-alt'}`}
                  aria-hidden='true'
                ></i>
              </button>
            )}
            {!locked && (
              <>
                <button
                  type='button'
                  className='btn btn-icon btn-sm btn-light-info'
                  title='ویرایش پرداخت'
                  aria-label='ویرایش پرداخت'
                  onClick={onEdit}
                >
                  <i className='las la-edit' aria-hidden='true'></i>
                </button>
                <button
                  type='button'
                  className='btn btn-icon btn-sm btn-light-danger'
                  title='حذف پرداخت'
                  aria-label='حذف پرداخت'
                  onClick={onDelete}
                >
                  <i className='las la-trash-alt' aria-hidden='true'></i>
                </button>
              </>
            )}
          </div>
        </td>
      </tr>
      {taxValidationError && (
        <tr className='mp-invoice-data-message-row has-error'>
          <td colSpan={columnCount}>
            <i className='las la-exclamation-triangle' aria-hidden='true'></i>
            <span>این ردیف پرداخت به سازمان ارسال نخواهد شد: {taxValidationError}</span>
          </td>
        </tr>
      )}
      {rowMessage && (
        <tr className={`mp-invoice-data-message-row ${rowMessageTone}`}>
          <td colSpan={columnCount}>
            <i
              className={`las ${registrationBadge?.icon || 'la-info-circle'}`}
              aria-hidden='true'
            ></i>
            <span>
              {rowMessage}
              {registrationBadge?.retryNeedsCheck && canRetry && (
                <> پس از بررسی کارپوشه، با دکمهٔ «تلاش مجدد» می‌توانید دوباره ثبت کنید.</>
              )}
            </span>
          </td>
        </tr>
      )}
      {expanded && (
        <tr className='mp-invoice-data-detail-row'>
          <td colSpan={columnCount}>
            <div className='mp-invoice-data-detail-row__content'>
              <div className='mp-invoice-data-detail-row__heading'>
                <strong>جزئیات پرداخت {index + 1}</strong>
                <span>فقط اطلاعات مرتبط و ثبت‌شده برای این روش پرداخت نمایش داده می‌شود.</span>
              </div>
              <InvoiceDetailsGrid
                items={[
                  {label: 'روش پرداخت', value: paymentMethod},
                  {label: 'مبلغ پرداختی', value: `${numberWithCommas(payment.price)} ریال`},
                  {label: 'سهم نقدی از پرداخت', value: `${numberWithCommas(payment.cash)} ریال`},
                  {
                    label: 'مبلغ ثبت در کارپوشهٔ سامانه مؤدیان (بدون مالیات)',
                    value: isSentInvoice
                      ? `${numberWithCommas(getPaymentRegisteredAmount(payment))} ریال`
                      : undefined,
                  },
                  {
                    label: 'سهم مالیات بر ارزش افزوده از پرداخت',
                    value: `${numberWithCommas(payment.vat)} ریال`,
                  },
                  {
                    label: 'شماره پیگیری یا مرجع',
                    value: referenceNumber ? (
                      <CopyableInvoiceValue value={referenceNumber} label='شماره پیگیری یا مرجع' />
                    ) : undefined,
                    wide: true,
                  },
                  {label: 'شماره سوئیچ پرداخت', value: payment.switchNumber, ltr: true},
                  {
                    label: 'شماره پذیرنده فروشگاهی',
                    value: payment.acquirerNumber,
                    ltr: true,
                  },
                  {label: 'شماره پایانه', value: payment.terminalNumber, ltr: true},
                  {
                    label: 'شماره کارت پرداخت‌کننده',
                    value: payment.cardNo || payment.payerCardNumber,
                    ltr: true,
                  },
                  {
                    label: 'شناسه پرداخت‌کننده',
                    value: payment.payerNationalCode || payment.payerId,
                    ltr: true,
                  },
                  {
                    label: 'شناسه تراکنش کارت‌خوان مرتبط',
                    value: payment.onlinePosTransactionId,
                    ltr: true,
                  },
                  {label: 'تاریخ و زمان پرداخت', value: payment.paymentDateTime, ltr: true},
                  {
                    label: 'وضعیت ثبت در سامانه مؤدیان',
                    value: registrationBadge?.label,
                  },
                  {
                    label: 'آخرین اقدام ثبت در سامانه',
                    value: payment.taxRegistration?.date,
                    ltr: true,
                  },
                  {
                    label: 'کد خطای سازمان',
                    value: payment.taxRegistration?.code,
                    ltr: true,
                  },
                  {label: 'توضیحات', value: payment.description, wide: true},
                  {
                    label: 'تعداد مستندات پیوست',
                    value: payment.files?.length
                      ? numberWithCommas(payment.files.length)
                      : undefined,
                  },
                ]}
              />
            </div>
          </td>
        </tr>
      )}
    </Fragment>
  )
}

export {InvoicePaymentRow}
